import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import "./ptDashboard.css";

const money = (v) =>
  `Rs ${Number(v || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const num = (v) => Number(v || 0).toLocaleString("en-PK");

const Kpi = ({ label, value, sub, tone, icon, onView }) => (
  <div className={`pt-kpi pt-tone-${tone}`}>
    <div className="pt-kpi-body">
      <div className="pt-kpi-label-row">
        <p className="pt-kpi-label">{label}</p>
        {onView ? (
          <button
            type="button"
            className="pt-kpi-eye"
            onClick={onView}
            aria-label={`View ${label} details`}
          >
            <i className="bi bi-eye"></i>
          </button>
        ) : null}
      </div>
      <h4 className="pt-kpi-value">{value}</h4>
      {sub ? <p className="pt-kpi-sub">{sub}</p> : null}
    </div>
    <div className="pt-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

const StatModal = ({ title, description, columns, rows, onClose }) => {
  if (!title) return null;
  return (
    <div className="pt-modal-back" onClick={onClose} role="presentation">
      <div
        className="pt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-modal-head">
          <h5>{title}</h5>
          <button type="button" className="pt-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        {description ? <p className="pt-modal-note">{description}</p> : null}
        {rows?.length ? (
          <div className="pt-table-wrap">
            <table className="pt-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.key || index}>
                    {columns.map((col) => (
                      <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pt-empty">Nothing to show for this period.</p>
        )}
      </div>
    </div>
  );
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function PtDashboard() {
  const { state } = useContext(NoteState);
  const { branch_id, first_name, last_name } = state;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [sops, setSops] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, sopRes] = await Promise.all([
        UserServices.trainerPortalSummary(branch_id || undefined),
        branch_id
          ? UserServices.getSops({
              branch_id,
              status: "1",
              limit: 5,
              page: 1,
            }).catch(() => null)
          : Promise.resolve(null),
      ]);

      const payload = summaryRes?.data?.data || null;
      setData(payload);

      const sopPayload = sopRes?.data?.data;
      const sopRows = Array.isArray(sopPayload)
        ? sopPayload
        : Array.isArray(sopPayload?.data)
          ? sopPayload.data
          : [];
      setSops(sopRows.slice(0, 5));
    } catch (err) {
      console.error(err);
      setData(null);
      setError(err?.response?.data?.message || "Could not load PT Dashboard.");
    } finally {
      setLoading(false);
    }
  }, [branch_id]);

  useEffect(() => {
    load();
  }, [load]);

  const retention = data?.retention || {};
  const sales = data?.sales || {};
  const checkins = data?.checkins_today || {};
  const cycle = data?.sessions_cycle || {};
  const period = cycle.period || {};
  const expiring = data?.expiring_soon || [];
  const clients = data?.clients || [];
  const checkinList = checkins.clients || [];
  const monthFrom = retention.from
    ? moment(retention.from).format("DD MMM")
    : moment().startOf("month").format("DD MMM");
  const monthTo = retention.to
    ? moment(retention.to).format("DD MMM")
    : moment().endOf("month").format("DD MMM");
  const salesFrom = sales.from
    ? moment(sales.from).format("DD MMM")
    : monthFrom;
  const salesTo = sales.to ? moment(sales.to).format("DD MMM") : monthTo;

  const detailView = (() => {
    if (detail === "active") {
      return {
        title: "Active Clients",
        description: `Clients whose PT package is still running today (${moment().format("DD MMM YYYY")}).`,
        columns: [
          { key: "name", label: "Client" },
          { key: "package_name", label: "Package" },
          {
            key: "end_date",
            label: "Ends",
            render: (row) => moment(row.end_date).format("DD MMM YYYY"),
          },
        ],
        rows: clients.map((row) => ({ ...row, key: row.client_id })),
      };
    }
    if (detail === "retention") {
      return {
        title: "Retention Rate",
        description: `Packages that ended from ${monthFrom} to ${monthTo}, plus renewals this month of a package that ended last month. A renewal counts in the month the package ended only when it is sold on or before the last day of that month. A renewal after that month counts in the next month.`,
        columns: [
          { key: "name", label: "Client" },
          { key: "package_name", label: "Package" },
          {
            key: "end_date",
            label: "Ended",
            render: (row) => moment(row.end_date).format("DD MMM YYYY"),
          },
          {
            key: "renewal_date",
            label: "Renewed on",
            render: (row) =>
              row.renewal_date ? moment(row.renewal_date).format("DD MMM YYYY") : "—",
          },
          {
            key: "renewed",
            label: "Renewed",
            render: (row) => (row.renewed ? "Yes" : "No"),
          },
        ],
        rows: (retention.clients || []).map((row) => ({
          ...row,
          key: `${row.client_id}-${row.end_date}`,
        })),
      };
    }
    if (detail === "sales") {
      return {
        title: "Sales",
        description: `PT package sales from ${salesFrom} to ${salesTo}. Today is ${money(sales.today)}.`,
        columns: [
          { key: "name", label: "Client" },
          { key: "package_name", label: "Package" },
          { key: "sale_type", label: "Type" },
          {
            key: "sale_date",
            label: "Date",
            render: (row) => moment(row.sale_date).format("DD MMM YYYY"),
          },
          {
            key: "amount",
            label: "Amount",
            render: (row) => money(row.amount),
          },
        ],
        rows: (sales.rows || []).map((row) => ({ ...row, key: row.id })),
      };
    }
    if (detail === "present") {
      return {
        title: "Present Today",
        description: `Active clients who checked in today, ${moment().format("DD MMM YYYY")}.`,
        columns: [
          { key: "name", label: "Client" },
          { key: "check_in_time", label: "Check-in" },
        ],
        rows: checkinList.map((row) => ({ ...row, key: row.client_id })),
      };
    }
    if (detail === "sessions") {
      return {
        title: "Sessions (26 → 25)",
        description: `Sessions you delivered from ${moment(period.start).format("DD MMM YYYY")} to ${moment(period.end).format("DD MMM YYYY")}. The payroll cycle always runs from the 26th to the 25th. Remaining is unused sessions on packages that are still running.`,
        columns: [
          { key: "label", label: "Item" },
          { key: "value", label: "Count" },
        ],
        rows: [
          { key: "conducted", label: "Delivered this cycle", value: num(cycle.conducted) },
          { key: "remaining", label: "Remaining on active packages", value: num(cycle.remaining) },
          { key: "entitled", label: "Total sessions on active packages", value: num(cycle.total_entitled) },
        ],
      };
    }
    return null;
  })();

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design pt-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="pt-topbar">
              <div>
                <p className="pt-eyebrow">Personal Trainer</p>
                <h2 className="pt-title">
                  <i className="bi bi-person-badge me-2"></i>
                  PT Dashboard
                </h2>
                <p className="pt-subtitle">
                  {moment().format("dddd, DD MMM YYYY")}
                  {first_name
                    ? ` · Welcome ${first_name}${last_name ? ` ${last_name}` : ""}`
                    : ""}
                </p>
              </div>
              <div className="pt-toolbar">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={load}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Refresh"}
                </button>
                <Link to="/session-tracker" className="btn btn-danger">
                  Open Session Tracker
                </Link>
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}

            {loading && !data ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Loading PT dashboard…</div>
              </div>
            ) : (
              <>
                <h3 className="pt-section-title">Today at a glance</h3>
                <div className="pt-kpi-grid">
                  <Kpi
                    label="Active Clients"
                    value={num(data?.active_clients)}
                    sub="Clients on a running PT package"
                    icon="bi-people"
                    tone="teal"
                    onView={() => setDetail("active")}
                  />
                  <Kpi
                    label="Retention Rate"
                    value={`${num(retention.rate)}%`}
                    sub={`${num(retention.renewed)} renewed / ${num(
                      retention.expired_this_month
                    )} expired · renewed by month end`}
                    icon="bi-arrow-repeat"
                    tone="blue"
                    onView={() => setDetail("retention")}
                  />
                  <Kpi
                    label="Sales"
                    value={money(sales.month)}
                    sub={`Today ${money(sales.today)} · ${salesFrom}–${salesTo}`}
                    icon="bi-cash-coin"
                    tone="red"
                    onView={() => setDetail("sales")}
                  />
                  <Kpi
                    label="Present Today"
                    value={num(checkins.present_count)}
                    sub="Checked-in clients"
                    icon="bi-person-check"
                    tone="green"
                    onView={() => setDetail("present")}
                  />
                  <Kpi
                    label="Sessions (26→25)"
                    value={num(cycle.conducted)}
                    sub={`${moment(period.start).format("DD MMM")} – ${moment(
                      period.end
                    ).format("DD MMM")} · Remaining ${num(cycle.remaining)}`}
                    icon="bi-lightning-charge"
                    tone="purple"
                    onView={() => setDetail("sessions")}
                  />
                </div>

                <h3 className="pt-section-title">Packages & attendance</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-6">
                    <div className="pt-panel">
                      <div className="pt-panel-head">
                        <h5>Expiring Soon (14 days)</h5>
                        <span className="pt-chip">{num(expiring.length)} clients</span>
                      </div>
                      {expiring.length ? (
                        <div className="pt-table-wrap">
                          <table className="pt-table">
                            <thead>
                              <tr>
                                <th>Client</th>
                                <th>Package</th>
                                <th>Ends</th>
                                <th>Left</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expiring.map((row) => (
                                <tr key={`${row.client_id}-${row.end_date}`}>
                                  <td>{row.name}</td>
                                  <td>{row.package_name}</td>
                                  <td>{moment(row.end_date).format("DD MMM YYYY")}</td>
                                  <td>{row.days_left}d</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="pt-empty">
                          No packages expiring in the next 14 days.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-xl-6">
                    <div className="pt-panel">
                      <div className="pt-panel-head">
                        <h5>Today&apos;s Check-ins</h5>
                        <span className="pt-chip">{num(checkinList.length)} present</span>
                      </div>
                      {checkinList.length ? (
                        <div className="pt-table-wrap">
                          <table className="pt-table">
                            <thead>
                              <tr>
                                <th>Client</th>
                                <th>Check-in</th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkinList.map((row) => (
                                <tr key={row.client_id}>
                                  <td>{row.name}</td>
                                  <td>{row.check_in_time || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="pt-empty">
                          No client check-ins recorded yet today.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <h3 className="pt-section-title">My clients</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-8">
                    <div className="pt-panel">
                      <div className="pt-panel-head">
                        <h5>Active Clients</h5>
                        <span className="pt-chip">
                          Assessments · sessions remaining
                        </span>
                      </div>
                      {clients.length ? (
                        <div className="pt-table-wrap">
                          <table className="pt-table">
                            <thead>
                              <tr>
                                <th>Client</th>
                                <th>Package</th>
                                <th>Ends</th>
                                <th>Sessions</th>
                                <th>Today</th>
                                <th>Assessment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clients.map((row) => (
                                <tr key={row.client_id}>
                                  <td>
                                    <Link to={`/client-home/${row.client_id}`}>
                                      {row.name}
                                    </Link>
                                  </td>
                                  <td>{row.package_name}</td>
                                  <td>{moment(row.end_date).format("DD MMM YYYY")}</td>
                                  <td>
                                    {num(row.sessions_delivered)} / {num(row.total_sessions)}
                                    <div className="small text-muted">
                                      {num(row.sessions_remaining)} left
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`pt-pill ${
                                        row.is_present_today
                                          ? "pt-pill-present"
                                          : "pt-pill-away"
                                      }`}
                                    >
                                      {row.is_present_today ? "Present" : "—"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="pt-row-actions">
                                      {row.has_post_assessment ? (
                                        <Link
                                          className="btn btn-sm btn-outline-secondary"
                                          to={`/viewAssessmentInfo/${row.client_id}`}
                                        >
                                          View
                                        </Link>
                                      ) : (
                                        <Link
                                          className="btn btn-sm btn-outline-danger"
                                          to={`/postAssessment/${row.client_id}`}
                                        >
                                          Add Assessment
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="pt-empty">No active PT clients found.</p>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-xl-4">
                    <div className="pt-panel">
                      <div className="pt-panel-head">
                        <h5>SOPs</h5>
                        <Link to="/sops" className="btn btn-sm btn-outline-danger">
                          View all
                        </Link>
                      </div>
                      {sops.length ? (
                        <div className="pt-sop-list">
                          {sops.map((row) => (
                            <Link
                              key={row.id}
                              to="/sops"
                              className="pt-sop-item"
                              title={stripHtml(row.content).slice(0, 120)}
                            >
                              <strong>{row.title || "SOP"}</strong>
                              <span>{row.sop_for || "Trainer"}</span>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="pt-empty">
                          No SOPs available for your branch yet.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <StatModal
        title={detailView?.title}
        description={detailView?.description}
        columns={detailView?.columns || []}
        rows={detailView?.rows || []}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
